import classNames from "classnames";
import {
	ClientWorkspaceModel,
	getWorkspacePageViewModel,
	loadWorkspace,
	SaveTileGroupModel,
	SaveTileGroupSpacePreferenceModel,
	saveWorkspace,
	SaveWorkspaceFailedResponse,
	SaveWorkspaceModel,
	SaveWorkspaceResponse,
	SpaceAccessKind,
	SpaceData,
	SpaceInfo,
	SpaceKind,
	TileGroupAccessKind,
	TileGroupSpacePreferenceData,
	TileGroupType,
	TileGroupWidth,
	WorkspaceViewModel
} from "client/Api/Workspace/workspace";
import {
	EMPTY_ARRAY,
	isNil,
	isNilOrWhiteSpace,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	type Navigation,
	NavigationCallback,
	PredefinedPropOptionKind,
	RouteParametersFromProps,
	connectToRouter,
	useBlockHistoryNavigation
} from "client/Navigation";
import {
	convertDimensionToPx,
	GridCellPixelValues,
	GridCellPixelValuesTracker,
	useGridCellPixelValuesState
} from "./GridCellPixelValuesTracker";
import {
	getGroupGridPixelStyles,
	getTileGridPixelStyles
} from "./utils";
import {
	AlertManager,
	EffectiveTheme,
	useAlerts,
	useEffectiveTheme,
	useResizeObserver,
	useStableImperativeCallback,
	useSynchronizedRef,
	useUnmountedCancellation,
	useUserTimezone,
	useValueEqualMemo,
	useWorkspaceMediaQuery,
	MediaQueryKind
} from "client/Hooks";
import {
	Header,
	SpaceTileGroupMenuOptions,
	TileGroupMenuItem
} from "./Header/Header";
import { LiveTileHeaderStyle } from "client/Api/Branding/branding";
import {
	DimensionLimitations,
	SpaceOptions,
	SpaceRenderState,
	TileGridConfiguration,
	TileGroupRenderState,
	widestTileGroupColumnSpan,
	WorkspaceInitializationModel
} from "./RenderState/common";
import { TileGroupGrid } from "./TileGroupGrid";
import { TimeZone } from "client/DateAndTime/timezones";
import { ItemCreationFab } from "../ItemCreation/ItemCreationFab";
import { PoweredBy } from "./PoweredBy";
import {
	convertDatabaseOrTempObjectIdToDto,
	DatabaseId,
	DatabaseOrTempObjectId,
	DatabaseOrTempObjectIdMap,
	TempId
} from "client_legacy/ndaw/tempOrDatabaseId";
import {
	convertStateToTileGroupSpacePreferenceData,
	convertStateToWorkspaceInitializationModel,
	convertTileGroupStateToSaveModel,
	convertToInitialization,
	convertToSpaceData
} from "./RenderState/conversion";
import {
	GlobalNavigationCriteria,
	useGlobalNavigationStore,
	WorkspaceSpaceNavigationHandlerOptions
} from "../Layout/TitleBar/GlobalNavigation/globalNavigationStore";
import { useMainMenuStore, WorkspaceHandler } from "../Layout/mainMenuStore";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { GlobalNavigationBarMode } from "client_legacy/TitleBar/GlobalNavigation/navigationItem";
import { TileContentDependencies, useTileContentDependencies } from "./useTileContentDependencies";
import { MobileNavigationBar } from "./MobileNavigationBar/MobileNavigationBar";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	addSpace,
	deleteSpace,
	updateSpace,
	updateSpacesWithPersistedData
} from "./RenderState/reducers/spaceData";
import { HttpRequestError } from "client/Api/httpClient";
import { localize } from "client_legacy/ndaw/culture";
import { BusyIndicator, Portal } from "client/Components";
import { TempIdToDatabaseIdMappingModel } from "client/Api/common";
import {
	addTileGroup,
	applyConfiguration,
	applyEditMode,
	applySpace,
	applyTileGroupVisibility,
	changeTileGroupName,
	deleteTile,
	deleteTileGroup,
	generateInitialState,
	persistTileStateOfEditableGroups,
	resizeTile,
	resizeTileGroup,
	updateIdsOfNewlyCreatedElements
} from "./RenderState/reducers/space";
import { useSharedTileGroupDefaultOrderList } from "./LeftSliders/SharedTileGroupDefaultOrderList";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { EditSharedTileGroupResultData, useEditSharedTileGroup } from "./LeftSliders/EditSharedTileGroup/EditSharedTileGroup";
import { useWorkspaceControllerStore, WorkspaceController } from "./useWorkspaceControllerStore";
import * as tileGroupSelectors from "./RenderState/selectors/tileGroup";
import { never } from "client_legacy/ndaw/errors";
import { GridDimension } from "client/Api/Workspace/common";
import { useEditTileSlider } from "./LeftSliders/EditTile/EditTileSlider";
import { isMobileDevice } from "client_legacy/ndaw/mobile";
import { Theme } from "client/Api/Branding/UserTheme/userTheme";
import { formatList } from "client/Misc/cultureSpecificUtils";
import { useAppStoreDialog } from "../Apps/Store/AppStoreDialog";
import { SpaceTemplateEnabledAppsAlert } from "./SpaceTemplateEnabledAppsAlert";
import { useSpaceTemplateEnabledApps } from "./useSpaceTemplateEnabledApps";
import styles from "./Workspace.module.less";
import { TileGridConfigurationTracker, useTileGridConfigurationState } from "./TileGridConfigurationTracker";
import {
	WorkspaceEvent,
	subscribe as workspaceSubscribe
} from "../Workspace/events";
import { SubscriptionTokenCollection } from "client_legacy/ndaw/subscriptionTokenCollection";
import { tryParseInt } from "client/Misc/parseUtils";
import { getWorkspacePageNavigation } from "./routing";
import { usePageTitle } from "client/Hooks/usePageTitle";
import { handlePageDataLoadingError } from "../ErrorPage/errorStore";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";

export type HistoryNavigationKind = "push" | "replace";

export type WorkspaceProps = {
	viewModel: WorkspaceViewModel;
	activeSpaceId: number | undefined;
	onChangeActiveSpaceId: (
		spaceId: number | undefined,
		changeKind: HistoryNavigationKind
	) => void;
};

const MIN_TILE_GRID_COLUMN_WIDTH_IN_PX = 45.5;
const MIN_TILE_GRID_COLUMN_WIDTH_IN_PX_MOBILE = 35.5;

const Workspace = (props: WorkspaceProps): ReactElement => {
	const {
		viewModel,
		activeSpaceId: activeSpaceIdFromRoute,
		onChangeActiveSpaceId: onChangeActiveSpaceIdFromProps
	} = props;

	const {
		settings,
		tileFactoryConfiguration,
		model
	} = viewModel;

	const { userPermissions, branding } = settings;

	const tileContentDependencies: TileContentDependencies = useTileContentDependencies(
		tileFactoryConfiguration
	);

	const [persistedTileGroupState, setPersistedTileGroupState] = useState<WorkspaceInitializationModel>(
		(): WorkspaceInitializationModel => {
			return convertToInitialization(
				model.tileGroups,
				model.maintenanceWindows ?? EMPTY_ARRAY,
				model.conditionalAccessDisabledApps ?? EMPTY_ARRAY,
				model.conditionalAccessDisabledTileGroups ?? EMPTY_ARRAY
			);
		}
	);

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [renderState, setRenderState] = useState<SpaceRenderState>(
		(): SpaceRenderState => {
			return generateInitialState(
				persistedTileGroupState,
				settings.automaticallyReorderTilesForNoneEditableTileGroups
			);
		}
	);

	const [tileGridCellTrackedPixelValues, setTileGridCellTrackedPixelValues] = useGridCellPixelValuesState();
	const [tileGridConfiguration, setTileGridConfiguration] = useTileGridConfigurationState();
	const workspaceRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const scrollableContainerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const tileGroupGridRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const { width: workspaceWidth } = useResizeObserver(workspaceRef);
	const { height: scrollContainerHeight } = useResizeObserver(scrollableContainerRef);
	const isMobileViewport: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const userTimezone: TimeZone = useUserTimezone();
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const setController: (controller: WorkspaceController, signal?: AbortSignal) => void = useWorkspaceControllerStore(
		x => x.setController
	);
	const [sharedTileGroupDefaultOrderList, openSharedTileGroupDefaultOrderList] = useSharedTileGroupDefaultOrderList();
	const [editSharedTileGroup, openEditSharedTileGroup] = useEditSharedTileGroup();
	const [editTileSlider, openEditTileSlider] = useEditTileSlider();
	const [appStoreDialog, openAppStoreDialog] = useAppStoreDialog();
	const effectiveTheme: EffectiveTheme = useEffectiveTheme();

	const [spaces, setSpaces] = useState<readonly SpaceData[]>(
		() => model.spaces
			.map(convertToSpaceData)
	);

	const defaultSpace: SpaceData | undefined = spaces[0];

	useBlockHistoryNavigation(renderState.isInEditMode);

	const onChangeActiveSpaceId = useCallback(
		(spaceId: number | undefined, historyKind: HistoryNavigationKind): void => {
			if (spaceId === defaultSpace?.id) {
				spaceId = undefined;
			}

			onChangeActiveSpaceIdFromProps(spaceId, historyKind);
		},
		[defaultSpace?.id, onChangeActiveSpaceIdFromProps]
	);

	const activeSpace = useMemo(
		(): SpaceData | undefined => {
			let activeSpace: SpaceData | undefined;
			if (isNotNil(activeSpaceIdFromRoute)) {
				activeSpace = spaces.find(
					(x) => x.id === activeSpaceIdFromRoute
				);
			}

			return activeSpace ?? defaultSpace;
		},
		[
			activeSpaceIdFromRoute,
			defaultSpace,
			spaces
		]
	);

	const {
		tileGroupGridCellPixelValues,
		numberOfColumnsThatFitAvailableSpace,
		tileGridCellPixelValues
	} = useMemo(
		(): {
			tileGroupGridCellPixelValues: GridCellPixelValues;
			tileGridCellPixelValues: GridCellPixelValues;
			numberOfColumnsThatFitAvailableSpace: number;
		} => {
			hasImplicitDependencyOn(workspaceWidth);

			const { current: element } = workspaceRef;
			const { current: scrollableContainer } = scrollableContainerRef;
			if (!element || !scrollableContainer) {
				return {
					tileGroupGridCellPixelValues: computeTileGroupGridCellPixelValues(
						tileGridCellTrackedPixelValues,
						tileGridConfiguration.dimensionLimits
					),
					numberOfColumnsThatFitAvailableSpace: 1,
					tileGridCellPixelValues: tileGridCellTrackedPixelValues
				};
			}

			const scrollContainerWidth: number = scrollableContainer
				.getBoundingClientRect()
				.width;

			const { paddingLeft, paddingRight } = window.getComputedStyle(element);
			const horizontalPadding: number = parseFloat(paddingLeft) + parseFloat(paddingRight);
			const availableWidth: number = Math.max(0, scrollContainerWidth - horizontalPadding);
			const minTileGridColumnWidthInPx: number = isMobileViewport
				? MIN_TILE_GRID_COLUMN_WIDTH_IN_PX_MOBILE
				: MIN_TILE_GRID_COLUMN_WIDTH_IN_PX;

			// For N tile grid cells we have N + 1 gutters, to make the math easier we subtract that 1 gutter
			// from the available width so 1 cell has 1 gutter (easier division)
			const amountOfTileGridColumnsThatFitInAvailableWidth: number = Math.floor(
				(availableWidth - tileGridCellTrackedPixelValues.columnGutter) / (minTileGridColumnWidthInPx + tileGridCellTrackedPixelValues.columnGutter)
			);

			const tileGridColumnCountPerRegularTileGroup: number = tileGridConfiguration.dimensionLimits
				.columns;

			// For N tile groups we have N - 1 gutter (where the gutter is a tile grid cell), to make the math easier
			// we add 1 gutter so 1 tile group has 1 gutter (easier division)
			const amountOfTileGroupGridColumnsThatFitInAvailableWidth: number = Math.max(
				1,
				Math.floor((amountOfTileGridColumnsThatFitInAvailableWidth + 1) / (tileGridColumnCountPerRegularTileGroup + 1))
			);

			// Each tile group has N cells in it and 1 cell as trailing gutter except for the last tile group
			const amountOfTileGridColumnsToBeRendered: number = Math.max(
				0,
				(amountOfTileGroupGridColumnsThatFitInAvailableWidth * (tileGridColumnCountPerRegularTileGroup + 1)) - 1
			);

			const widthConsumedByTileGridColumnGutters: number = (amountOfTileGridColumnsToBeRendered + 1) * tileGridCellTrackedPixelValues.columnGutter;

			const computedTileGridCellColumnWidthRaw: number = (availableWidth - widthConsumedByTileGridColumnGutters)
				/ amountOfTileGridColumnsToBeRendered;

			// Safeguard to never end up with weird values (infinite/negative, 0 etc)
			const computedTileGridCellColumnWidth: number = Number.isFinite(computedTileGridCellColumnWidthRaw)
				? Math.max(minTileGridColumnWidthInPx, computedTileGridCellColumnWidthRaw)
				: minTileGridColumnWidthInPx;

			const tileGridCellPixelValues: GridCellPixelValues = new GridCellPixelValues(
				computedTileGridCellColumnWidth,
				tileGridCellTrackedPixelValues.rowHeight,
				tileGridCellTrackedPixelValues.columnGutter,
				tileGridCellTrackedPixelValues.rowGutter
			);

			return {
				tileGroupGridCellPixelValues: computeTileGroupGridCellPixelValues(
					tileGridCellPixelValues,
					tileGridConfiguration.dimensionLimits
				),
				numberOfColumnsThatFitAvailableSpace: amountOfTileGroupGridColumnsThatFitInAvailableWidth,
				tileGridCellPixelValues
			};
		},
		[
			tileGridCellTrackedPixelValues,
			tileGridConfiguration.dimensionLimits,
			isMobileViewport,
			workspaceWidth
		]
	);

	const tileGroupGridDimensionLimitsUnstable = useMemo(
		(): DimensionLimitations => {
			const numberOfColumns: number = Math.max(
				numberOfColumnsThatFitAvailableSpace,
				renderState.isInEditMode
					? widestTileGroupColumnSpan
					: 1
			);

			return {
				minColumns: numberOfColumns,
				maxColumns: numberOfColumns,
				minRows: 0,
				maxRows: Number.MAX_VALUE
			};
		},
		[
			renderState.isInEditMode,
			numberOfColumnsThatFitAvailableSpace
		]
	);

	const tileGroupGridDimensionLimits = useValueEqualMemo(
		(): DimensionLimitations => tileGroupGridDimensionLimitsUnstable,
		[tileGroupGridDimensionLimitsUnstable]
	);

	const tileGridConfigurationRef: ReadonlyRefObject<TileGridConfiguration> = useSynchronizedRef(tileGridConfiguration);
	const tileGroupGridDimensionLimitsRef: ReadonlyRefObject<DimensionLimitations> = useSynchronizedRef(tileGroupGridDimensionLimits);

	const rebuildFromBackend = useCallback(
		async (): Promise<void> => {
			setIsBusy(true);

			try {
				const workspaceModel: ClientWorkspaceModel = await loadWorkspace(globalCancellation);

				const persistedTileGroupState: WorkspaceInitializationModel = convertToInitialization(
					workspaceModel.tileGroups,
					workspaceModel.maintenanceWindows ?? EMPTY_ARRAY,
					workspaceModel.conditionalAccessDisabledApps ?? EMPTY_ARRAY,
					workspaceModel.conditionalAccessDisabledTileGroups ?? EMPTY_ARRAY
				);

				setPersistedTileGroupState(persistedTileGroupState);

				const renderState: SpaceRenderState = generateInitialState(
					persistedTileGroupState,
					settings.automaticallyReorderTilesForNoneEditableTileGroups,
					// We use refs for the configurations as rebuildFromBackend and it's dependencies can be
					// captured by other functions (and it is async). So when this code is executed the
					// real configurations could have been changed.
					// A concrete example is that while the app store is open, the "SpacePanel" (SubNavigation) is hidden
					// resulting in the Workspace to become wider (i.e. potentially allowing for more columns)
					tileGridConfigurationRef.current,
					tileGroupGridDimensionLimitsRef.current
				);

				setRenderState(renderState);

				const spacesData: SpaceData[] = workspaceModel.spaces
					.map(convertToSpaceData);

				setSpaces(spacesData);
				setIsBusy(false);
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_SorryWeFailedToRefreshYourWorkspacePleaseTryToRefreshTheWorkspaceManually"));
				setIsBusy(false);
				throw e;
			}
		},
		[
			alertManager,
			globalCancellation,
			tileGridConfigurationRef,
			settings.automaticallyReorderTilesForNoneEditableTileGroups,
			tileGroupGridDimensionLimitsRef
		]
	);

	const activeSpaceId: number | undefined = activeSpace?.id;

	const {
		apps: templateEnabledAppsToConfigureForSpace,
		clearEnabledApps: dismissTemplateAlertForSpace
	} = useSpaceTemplateEnabledApps(activeSpaceId);

	useEffect(
		(): void => {
			const isRouteInSync: boolean = isNotNil(activeSpaceIdFromRoute) === isNotNil(activeSpaceId)
				&& (
					isNil(activeSpaceIdFromRoute)
						|| (
							isNotNil(activeSpaceId)
								&& activeSpaceIdFromRoute === activeSpaceId
						)
				);

			if (!isRouteInSync) {
				onChangeActiveSpaceId(activeSpaceId, "replace");
			}
		},
		[
			activeSpaceId,
			activeSpaceIdFromRoute,
			onChangeActiveSpaceId
		]
	);

	const supportsEditMode: boolean = useMemo(
		(): boolean => userPermissions.canEditOwnWorkspace
			&& !isMobileDevice(),
		[userPermissions.canEditOwnWorkspace]
	);

	const isInResponsiveMode = useMemo(
		(): boolean => {
			const DEFAULT_TILE_GRID_COLUMNS = 8;

			for (const group of renderState.groups) {
				const { tileGrid } = group;
				if (tileGrid.dimension.columnSpan < DEFAULT_TILE_GRID_COLUMNS) {
					return true;
				}

				for (const tile of tileGrid.tiles) {
					if (tile.dimension.kind === "responsive") {
						return true;
					}
				}
			}

			return false;
		},
		[renderState.groups]
	);

	const isSpaceEditable = useCallback(
		(spaceIsh: { kind: SpaceKind; accessKind: SpaceAccessKind; }): boolean => {
			if (!supportsEditMode || isInResponsiveMode) {
				return false;
			}

			if (spaceIsh.accessKind === SpaceAccessKind.Edit) {
				return true;
			}

			switch (spaceIsh.kind) {
				case SpaceKind.Personal:
					return false;
				case SpaceKind.Shared:
					return userPermissions.canManageSharedSpaces;
				default:
					never(spaceIsh.kind);
			}
		},
		[
			isInResponsiveMode,
			supportsEditMode,
			userPermissions.canManageSharedSpaces
		]
	);

	const accessKindPerTileGroupUnstable = useMemo(
		(): Map<DatabaseOrTempObjectId, TileGroupAccessKind> => {
			return fromArray(
				() => new DatabaseOrTempObjectIdMap<TileGroupAccessKind>(),
				renderState.groups,
				(x) => x.data.id,
				(x) => x.data.accessKind
			);
		},
		[renderState.groups]
	);

	const accessKindPerTileGroup = useValueEqualMemo(
		() => accessKindPerTileGroupUnstable,
		[accessKindPerTileGroupUnstable]
	);

	const isAnyVisibleTileGroupEditableInSpace = useCallback(
		(space: SpaceData): boolean => {
			const hasEditableTileGroup: boolean = space.tileGroupsPreferences
				.some(
					x => !x.isHidden
						&& accessKindPerTileGroup.get(x.tileGroupId) === TileGroupAccessKind.Edit
				);

			return supportsEditMode
				&& !isInResponsiveMode
				&& hasEditableTileGroup;
		},
		[
			accessKindPerTileGroup,
			isInResponsiveMode,
			supportsEditMode
		]
	);

	const navigateToSpace = useCallback(
		(id: number): void => {
			onChangeActiveSpaceId(id, "push");
		},
		[onChangeActiveSpaceId]
	);

	const validateHandler = useCallback(
		(renderState: SpaceRenderState): InputValidationResult => {
			const hasGroupsWithEmptyName = renderState.groups
				.some(group => isNilOrWhiteSpace(group.data.name));

			if (hasGroupsWithEmptyName) {
				return {
					kind: InputValidationResultKind.Invalid,
					errorMessage: localize("_EmptyGroupTitleErrorMessage")
				};
			}

			return {
				kind: InputValidationResultKind.Valid
			};
		},
		[]
	);

	const allowEditMode = useMemo(
		(): boolean => {
			if (!activeSpace) {
				return false;
			}

			return isSpaceEditable(activeSpace)
				|| isAnyVisibleTileGroupEditableInSpace(activeSpace);
		},
		[
			activeSpace,
			isAnyVisibleTileGroupEditableInSpace,
			isSpaceEditable
		]
	);

	const setEditMode = useCallback(
		(isEditMode: boolean): void => {
			if (isEditMode && !allowEditMode) {
				throw new Error("Edit mode is not allowed in the current usage");
			}

			setRenderState((x) => applyEditMode(x, isEditMode));
		},
		[allowEditMode]
	);

	const showSaveError = useCallback(
		(reason?: string): void => {
			alertManager.showError(
				[
					localize("_CannotSaveWorkspace"),
					isNotNil(reason) ? reason + "." : null,
					localize("_PleaseTryToRefreshTheWorkspaceOrContactTheAdmin")
				]
					.filter(isNotNil)
					.join(" "),
				isNotNil(reason)
					// increase the alert timeout so a user is able to comprehend the message
					? 8
					: undefined
			);
		},
		[alertManager]
	);

	const showMissingTilesError = useCallback(
		(missingTileIds: number[]): void => {
			const tileIdToNameMap: Map<number, string> = fromArray(
				renderState.groups
					.flatMap(g => g.tileGrid.tiles),
				g => g.data.id,
				g => g.data.name
			);

			const missingTileNames: string[] = missingTileIds
				.map(
					id => tileIdToNameMap.get(id)
				)
				.filter(isNotNil);

			const template: string = localize("_TheFollowingTilesAreRemovedFromDatabase");
			const message: string = template.replace("{0}", formatList(missingTileNames));

			showSaveError(message);
		},
		[
			renderState.groups,
			showSaveError
		]
	);

	const saveRequestErrorHandler = useCallback(
		(error: HttpRequestError): boolean => {
			let detailedErrorShown: boolean = false;

			if (error.statusCode === 400) {
				try {
					const failResponse: SaveWorkspaceFailedResponse = error.readJson<SaveWorkspaceFailedResponse>();

					if (failResponse.missingTileIds && failResponse.missingTileIds.length > 0) {
						showMissingTilesError(failResponse.missingTileIds);
						detailedErrorShown = true;
					}

					if (failResponse.reasons && failResponse.reasons.length > 0) {
						failResponse.reasons.forEach(message => showSaveError(message));
						detailedErrorShown = true;
					}
				} catch (exception) {
					console.error(exception);
				}
			} else if (error.statusCode === 403) {
				alertManager.showError(localize("_YouDontHavePermissionToEditYourWorkspace"));
				detailedErrorShown = true;
			}
			return detailedErrorShown;
		},
		[
			alertManager,
			showMissingTilesError,
			showSaveError
		]
	);

	const saveHandler = useStableImperativeCallback(
		async (doNotLeaveEditMode: boolean = false): Promise<TempIdToDatabaseIdMappingModel[]> => {
			const validationResult: InputValidationResult = validateHandler(renderState);

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				alertManager.showError(validationResult.errorMessage);
				throw new Error("Workspace validation has failed.");
			}

			let newRenderState: SpaceRenderState = persistTileStateOfEditableGroups(renderState);
			const groupSaveModels: SaveTileGroupModel[] = newRenderState
				.groups
				.map(convertTileGroupStateToSaveModel);

			const tileGroupsPreferences: readonly TileGroupSpacePreferenceData[] = convertStateToTileGroupSpacePreferenceData(
				newRenderState
			);

			const spaceOptions: OptionalModel<SpaceOptions> = newRenderState.options;
			if (!spaceOptions.isSet) {
				throw new Error("Cannot save changes, there is no active space.");
			}

			const spaceOptionsValue: SpaceOptions = spaceOptions.value;

			const saveModel: SaveWorkspaceModel = {
				tileGroups: groupSaveModels,
				space: isSpaceEditable(spaceOptionsValue)
					? {
						isSet: true,
						value: {
							id: spaceOptionsValue.id,
							kind: spaceOptionsValue.kind,
							tileGroupsPreferences: tileGroupsPreferences
								.map((data: TileGroupSpacePreferenceData): SaveTileGroupSpacePreferenceModel => ({
									tileGroupId: convertDatabaseOrTempObjectIdToDto(data.tileGroupId),
									isHidden: data.isHidden
								}))
						}
					}
					: { isSet: false }
			};

			try {
				setIsBusy(true);
				const response: SaveWorkspaceResponse = await saveWorkspace(saveModel, globalCancellation);

				newRenderState = updateIdsOfNewlyCreatedElements(
					newRenderState,
					response.addedItemsIdsMap
				);

				setRenderState(
					renderStateToOverwrite => {
						if (renderStateToOverwrite !== renderState) {
							throw new Error("Render state has mutated during saving.");
						}

						return newRenderState;
					}
				);

				setPersistedTileGroupState(
					convertStateToWorkspaceInitializationModel(newRenderState)
				);

				setSpaces(
					spaces => updateSpacesWithPersistedData(
						spaces,
						spaceOptionsValue.id,
						tileGroupsPreferences,
						response.addedItemsIdsMap
					)
				);

				if (!doNotLeaveEditMode) {
					setEditMode(false);
				}

				setIsBusy(false);

				return response.addedItemsIdsMap;
			} catch(e) {
				if (isCancelledError(e)) {
					throw e;
				}

				let wasHandled: boolean = false;

				if (e instanceof HttpRequestError) {
					wasHandled = saveRequestErrorHandler(e);
				}

				if (!wasHandled) {
					showSaveError();
				}

				setIsBusy(false);

				throw e;
			}
		}
	);

	const cancelHandler = useCallback(
		(doNotLeaveEditMode: boolean = false): void => {
			let state: SpaceRenderState = generateInitialState(
				persistedTileGroupState,
				settings.automaticallyReorderTilesForNoneEditableTileGroups,
				tileGridConfiguration,
				tileGroupGridDimensionLimits
			);

			if (doNotLeaveEditMode) {
				state = applyEditMode(state, true);
			}

			if (activeSpace) {
				const options: SpaceOptions = {
					id: activeSpace.id,
					canRearrangeTileGroupsInSpace: isSpaceEditable(activeSpace),
					kind: activeSpace.kind,
					name: activeSpace.name,
					accessKind: activeSpace.accessKind
				};

				state = applySpace(
					state,
					{
						isSet: true,
						value: options
					},
					activeSpace.tileGroupsPreferences
				);
			} else {
				state = applySpace(
					state,
					{ isSet: false },
					EMPTY_ARRAY
				);
			}

			setRenderState(state);
		},
		[
			activeSpace,
			isSpaceEditable,
			persistedTileGroupState,
			settings.automaticallyReorderTilesForNoneEditableTileGroups,
			tileGridConfiguration,
			tileGroupGridDimensionLimits
		]
	);

	const spaceAddedHandler = useCallback(
		(newSpace: SpaceInfo): void => {
			setSpaces(spaces => addSpace(spaces, newSpace));
			navigateToSpace(newSpace.id);
		},
		[navigateToSpace]
	);

	const spaceDeletedHandler = useCallback(
		(spaceId: number): void => {
			if (activeSpaceId === spaceId) {
				if (renderState.isInEditMode) {
					cancelHandler();
				}
			}

			setSpaces(spaces => deleteSpace(spaces, spaceId));
		},
		[
			activeSpaceId,
			cancelHandler,
			renderState.isInEditMode
		]
	);

	const spaceUpdatedHandler = useCallback(
		(updatedSpace: SpaceInfo): void => {
			if (renderState.isInEditMode
				&& activeSpaceId === updatedSpace.id
				&& !isSpaceEditable(updatedSpace)
			) {
				cancelHandler();
			}

			setSpaces(spaces => updateSpace(spaces, updatedSpace));
		},
		[
			activeSpaceId,
			cancelHandler,
			isSpaceEditable,
			renderState.isInEditMode
		]
	);

	const scrollToHandler = useCallback(
		(id: DatabaseOrTempObjectId, groups: readonly TileGroupRenderState[]): void => {
			if (!tileGroupGridRef.current
				|| !scrollableContainerRef.current
			) {
				return;
			}

			const { rowHeightWithGutter } = tileGroupGridCellPixelValues;

			const scrollContainer: HTMLDivElement = scrollableContainerRef.current;
			const tileGroupsContainer: HTMLDivElement = tileGroupGridRef.current;
			const tileGroup: TileGroupRenderState | undefined = groups
				.find((x) => x.data.id.equals(id));

			if (!tileGroup) {
				console.error(`Cannot scroll to '${id.toString()}' because it is not found in the tile groups collection`);
				return;
			}

			const { top: scrollContainerTop } = scrollContainer.getBoundingClientRect();
			const { top: tileGroupsContainerTop } = tileGroupsContainer.getBoundingClientRect();
			const scrollTop: number = scrollContainer.scrollTop;
			const tileGroupGridOffset: number = (tileGroupsContainerTop + scrollTop) - scrollContainerTop;
			const targetScrollTop: number = tileGroupGridOffset + (tileGroup.position.row * rowHeightWithGutter);

			scrollContainer
				.scrollTo({
					top: targetScrollTop,
					behavior: "smooth"
				});
		},
		[tileGroupGridCellPixelValues]
	);

	const addNewTileGroupHandler = useCallback(
		(kind: TileGroupType): void => {
			const canAddANewGroup: boolean = isNotNil(activeSpace) &&
				isSpaceEditable(activeSpace);

			if (!canAddANewGroup) {
				throw new Error("Cannot add a new group to a non editable space.");
			}

			let name: string;
			switch (kind) {
				case TileGroupType.Personal:
					name = localize("_NewGroup");
					break;
				case TileGroupType.Shared:
					name = localize("_NewSharedGroup");
					break;
				default:
					never(kind);
			}

			const newGroupId = new TempId(newGuid());

			setEditMode(true);

			const newRenderState: SpaceRenderState = addTileGroup(
				renderState,
				newGroupId,
				kind,
				name
			);

			setRenderState(newRenderState);

			scrollToHandler(newGroupId, newRenderState.groups);
		},
		[
			activeSpace,
			isSpaceEditable,
			renderState,
			scrollToHandler,
			setEditMode
		]
	);

	const addNewTileHandler = useCallback(
		async (tileGroupId?: DatabaseOrTempObjectId): Promise<void> => {
			let targetTileGroupDatabaseId: number | null = null;
			let idMap: TempIdToDatabaseIdMappingModel[] | undefined = undefined;

			if (renderState.isInEditMode) {
				idMap = await saveHandler(true);
			}

			if (tileGroupId) {
				if (tileGroupId.hasDatabaseId) {
					targetTileGroupDatabaseId = tileGroupId.databaseId;
				} else {
					const record: TempIdToDatabaseIdMappingModel | undefined = idMap?.find(x => x.tempId === tileGroupId.tempId);

					if (!record) {
						throw new Error("Cannot add tiles to the group that not saved to the database.");
					}

					targetTileGroupDatabaseId = record.databaseId;
				}
			}

			await openAppStoreDialog(
				isNotNil(targetTileGroupDatabaseId)
					? {
						initialView: {
							kind: "add-new-tile",
							targetTileGroupId: targetTileGroupDatabaseId
						}
					} : {}
			);
		},
		[
			openAppStoreDialog,
			renderState.isInEditMode,
			saveHandler
		]
	);

	const ensureInEditModeAndBackendInSync = useCallback(
		async (): Promise<void> => {
			if (!allowEditMode) {
				throw new Error("Edit mode not allowed");
			}

			if (renderState.isInEditMode) {
				await saveHandler(true);
			} else {
				setEditMode(true);
			}
		},
		[
			allowEditMode,
			renderState.isInEditMode,
			saveHandler,
			setEditMode
		]
	);

	const editTileHandler = useCallback(
		async (id: number): Promise<void> => {
			await ensureInEditModeAndBackendInSync();
			const result: TryResult<void> = await openEditTileSlider({ id });

			if (!result.wasSuccessful) {
				return;
			}

			await rebuildFromBackend();
			setEditMode(true);
		},
		[
			ensureInEditModeAndBackendInSync,
			openEditTileSlider,
			rebuildFromBackend,
			setEditMode
		]
	);

	const resizeTileHandler = useCallback(
		(id: number, dimension: GridDimension): void => {
			setRenderState(
				(x) => resizeTile(
					x,
					id,
					dimension
				)
			);
		},
		[]
	);

	const deleteTileHandler = useCallback(
		(id: number): void => {
			setRenderState(
				(x) => deleteTile(
					x,
					id
				)
			);
		},
		[]
	);

	const changeTileGroupVisibilityHandler = useCallback(
		(tileGroupId: DatabaseOrTempObjectId, isVisible: boolean): void => {
			setRenderState(
				renderState => {
					if (!renderState.isInEditMode) {
						throw new Error("Unable to change tile group visibility from the read mode.");
					}

					return applyTileGroupVisibility(
						renderState,
						tileGroupId,
						isVisible
					);
				}
			);
		},
		[]
	);

	const tileGroupNameChangeHandler = useCallback(
		(tileGroupId: DatabaseOrTempObjectId, newName: string): void => {
			setRenderState(
				renderState => changeTileGroupName(
					renderState,
					tileGroupId,
					newName
				)
			);
		},
		[]
	);

	const tileGroupResizeHandler = useCallback(
		(tileGroupId: DatabaseOrTempObjectId, width: TileGroupWidth): void => {
			setRenderState(
				renderState => resizeTileGroup(
					renderState,
					tileGroupId,
					width
				)
			);
		},
		[]
	);

	const tileGroupDeleteHandler = useCallback(
		(tileGroupId: DatabaseOrTempObjectId): void => {
			setRenderState(renderState => deleteTileGroup(renderState, tileGroupId));
		},
		[]
	);

	const sharedTileGroupEditHandler = useCallback(
		async (tileGroupId: DatabaseOrTempObjectId): Promise<void> => {
			if (renderState.isInEditMode) {
				const newIdsMap: TempIdToDatabaseIdMappingModel[] = await saveHandler(true);

				if (!tileGroupId.hasDatabaseId) {
					const tempId: Guid = tileGroupId.tempId;

					const newId: TempIdToDatabaseIdMappingModel | undefined = newIdsMap.find(
						x => x.tempId === tempId
					);

					if (newId) {
						tileGroupId = new DatabaseId(newId.databaseId);
					}
				}
			}

			if (!tileGroupId.hasDatabaseId) {
				throw new Error("Cannot manage tile group that is not saved to the database");
			}

			const result: TryResult<EditSharedTileGroupResultData> = await openEditSharedTileGroup({
				id: tileGroupId.databaseId,
				isInPersonalSpace: activeSpace?.kind === SpaceKind.Personal
			});

			if (result.wasSuccessful) {
				rebuildFromBackend();
			}
		},
		[
			activeSpace?.kind,
			openEditSharedTileGroup,
			rebuildFromBackend,
			renderState.isInEditMode,
			saveHandler
		]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			const controller: WorkspaceController = {
				activeSpaceId: activeSpaceId ?? null,
				refresh: (): Promise<void> => {
					return rebuildFromBackend();
				},
				switchToEditMode: (): void => {
					setEditMode(true);
				},
				cancelEditMode: (): void => {
					if (renderState.isInEditMode) {
						cancelHandler();
					}
				}
			};

			return setController(controller);
		},
		[
			activeSpaceId,
			cancelHandler,
			rebuildFromBackend,
			renderState.isInEditMode,
			setController,
			setEditMode
		]
	);

	const setWorkspaceHandler: ((handler: WorkspaceHandler) => void) = useMainMenuStore((x) => x.setWorkspaceHandler);
	const setModeForGlobalNavigation: (mode: GlobalNavigationBarMode) => void = useGlobalNavigationStore((x) => x.setMode);
	const setWorkspaceSpaceNavigationHandlerOptions: ((value: WorkspaceSpaceNavigationHandlerOptions) => void) = useGlobalNavigationStore((x) => x.setWorkspaceSpaceNavigationHandlerOptions);
	const setActiveCriteria: (criteria: GlobalNavigationCriteria) => (() => void) = useGlobalNavigationStore((x) => x.setActiveCriteria);

	useEffect(
		(): void => {
			setWorkspaceHandler({
				openDefaultSpace: () => {
					const space: SpaceData | undefined = defaultSpace;
					if (!space) {
						return;
					}

					navigateToSpace(space.id);
				}
			});
		},
		[
			defaultSpace,
			navigateToSpace,
			setWorkspaceHandler
		]
	);

	useEffect(
		(): void => {
			setModeForGlobalNavigation(
				renderState.isInEditMode
					? GlobalNavigationBarMode.EditSpaceMode
					: GlobalNavigationBarMode.Normal
			);
		},
		[renderState.isInEditMode, setModeForGlobalNavigation]
	);

	useEffect(
		(): void => {
			setWorkspaceSpaceNavigationHandlerOptions({
				activate: (id) => {
					const space: SpaceData | undefined = spaces.find(
						(x) => x.id === id
					);

					if (!space) {
						throw new Error(`Space is not present in set`);
					}

					navigateToSpace(space.id);
				},
				canEnterEditModeInSpace: (id) => {
					const space: SpaceData | undefined = spaces.find(
						(x) => x.id === id
					);

					if (!space) {
						return false;
					}

					return isSpaceEditable(space)
						|| isAnyVisibleTileGroupEditableInSpace(space);
				},
				saveSpaceChangesCallback: async (): Promise<void> => void(await saveHandler(true)),
				rollbackSpaceChangesCallback: () => cancelHandler(true)
			});
		},
		[
			cancelHandler,
			isAnyVisibleTileGroupEditableInSpace,
			isSpaceEditable,
			navigateToSpace,
			saveHandler,
			setWorkspaceSpaceNavigationHandlerOptions,
			spaces
		]
	);

	useEffect(
		(): (() => void) | void => {
			if (isNil(activeSpaceId)) {
				return;
			}

			return setActiveCriteria({
				type: "space",
				spaceId: activeSpaceId
			});
		},
		[activeSpaceId, setActiveCriteria]
	);

	useEffect(
		(): void => {
			setRenderState(
				(x): SpaceRenderState => applyConfiguration(
					x,
					tileGroupGridDimensionLimits,
					tileGridConfiguration
				)
			);
		},
		[tileGroupGridDimensionLimits, tileGridConfiguration]
	);

	useEffect(
		(): void => {
			if (activeSpace) {
				const spaceOptions: SpaceOptions = {
					id: activeSpace.id,
					kind: activeSpace.kind,
					name: activeSpace.name,
					canRearrangeTileGroupsInSpace: isSpaceEditable(activeSpace),
					accessKind: activeSpace.accessKind
				};

				setRenderState(
					x => applySpace(
						x,
						{
							isSet: true,
							value: spaceOptions
						},
						activeSpace.tileGroupsPreferences
					)
				);
			} else {
				setRenderState(
					x => applySpace(
						x,
						{ isSet: false },
						EMPTY_ARRAY
					)
				);
			}
		},
		[activeSpace, isSpaceEditable]
	);

	useEffect(
		(): () => void => {
			const subscriptionTokens: SubscriptionTokenCollection = registerWorkspaceSubscriptions({
				spaceAdded: spaceAddedHandler,
				spaceUpdated: spaceUpdatedHandler,
				spaceDeleted: spaceDeletedHandler
			});

			return (): void => subscriptionTokens.destroy();
		},
		[
			spaceAddedHandler,
			spaceUpdatedHandler,
			spaceDeletedHandler
		]
	);

	const headerWidthInPx = useMemo(
		(): number => convertDimensionToPx(renderState.dimension, tileGroupGridCellPixelValues)
			.width,
		[renderState.dimension, tileGroupGridCellPixelValues]
	);

	const sortedVisibleTileGroups = useMemo(
		(): readonly TileGroupRenderState[] => {
			return sortBy(
				sortBy(
					renderState.groups
						.filter((x) => x.isVisible),
					(x) => x.position.column
				),
				(x) => x.position.row
			);
		},
		[renderState.groups]
	);

	const tileGroupMenuOptions = useMemo(
		(): SpaceTileGroupMenuOptions => {
			const tileGroups: TileGroupMenuItem[] = [];

			const result: SpaceTileGroupMenuOptions = {
				canCreateNewPersonalGroup: false,
				canCreateNewSharedGroup: false,
				canChangeDefaultSharedGroupsOrder: false,
				tileGroups
			};

			if (!activeSpace) {
				return result;
			}

			const canEditSpace: boolean = isSpaceEditable(activeSpace);

			const isPersonalSpace: boolean = activeSpace.kind === SpaceKind.Personal;
			const {
				canCreateSharedGroups,
				canChangeDefaultSharedGroupsOrder
			} = settings.userPermissions;

			if (!allowEditMode) {
				return result;
			}

			if (canChangeDefaultSharedGroupsOrder && isPersonalSpace) {
				result.canChangeDefaultSharedGroupsOrder = true;
			}

			if (canEditSpace) {
				if (isPersonalSpace) {
					result.canCreateNewPersonalGroup = true;
				}

				if (canCreateSharedGroups) {
					result.canCreateNewSharedGroup = true;
				}

				const orderedGroups: readonly TileGroupRenderState[] = sortBy(renderState.groups, x => x.order);

				for (const tileGroup of orderedGroups){
					if (tileGroupSelectors.isHiddenFromListing(
						tileGroup,
						renderState.isInEditMode,
						renderState.options
					)) {
						continue;
					}

					if (isPersonalSpace || tileGroup.data.kind === TileGroupType.Shared) {
						tileGroups.push({
							id: tileGroup.data.id,
							isVisible: tileGroup.isVisible,
							name: tileGroup.data.name,
							type: tileGroup.data.kind
						});
					}
				}
			}

			return result;
		},
		[
			activeSpace,
			allowEditMode,
			isSpaceEditable,
			renderState.groups,
			renderState.isInEditMode,
			renderState.options,
			settings.userPermissions
		]
	);

	const dismissTemplateAlertForSpaceHandler = useCallback(
		(): void => {
			if (isNil(activeSpaceId)) {
				return;
			}

			dismissTemplateAlertForSpace(activeSpaceId);
		},
		[
			activeSpaceId,
			dismissTemplateAlertForSpace
		]
	);

	const templateAlertAppClickHandler = useCallback(
		async (installedAppId: number): Promise<void> => {
			await openAppStoreDialog({
				initialView: {
					kind: "edit-app",
					installedAppId: installedAppId
				}
			});
		},
		[openAppStoreDialog]
	);

	const hasBackground: boolean = branding.backgroundUrl !== null
		&& effectiveTheme === Theme.Light
		&& !renderState.isInEditMode;

	return (
		<>
			<div
				{...setTrackingId("workspace-page")}
				className={styles.root}
			>
				<div className={styles.navigation}>
					{isNotNil(scrollableContainerRef.current)
						&& isNotNil(tileGroupGridRef.current)
						&& (
							<MobileNavigationBar
								sortedVisibleTileGroups={sortedVisibleTileGroups}
								scrollContainer={scrollableContainerRef.current}
								tileGroupsContainer={tileGroupGridRef.current}
								tileGroupGridCellPixelValues={tileGroupGridCellPixelValues}
								onScrollToTileGroup={scrollToHandler}
							/>
						)
					}
				</div>
				<div className={styles.content}>
					<div
						{...setTrackingId("space-layout")}
						className={classNames(
							styles.spaceLayout,
							"preserve-space-for-mobile-plus-button",
							{
								["colored-live-tile-header-style"]: branding.liveTileHeaderStyle === LiveTileHeaderStyle.coloredBackground
									&& effectiveTheme === Theme.Light,
								[styles.spaceLayout_hasImage]: hasBackground
							}
						)}
						style={{
							...getTileGridPixelStyles(tileGridCellPixelValues),
							...getGroupGridPixelStyles(tileGroupGridCellPixelValues),
							["--_workspace-background"]: hasBackground ? `url(${branding.backgroundUrl})` : undefined
						}}
						ref={scrollableContainerRef}
					>
						{isNotNil(templateEnabledAppsToConfigureForSpace)
							&& templateEnabledAppsToConfigureForSpace.length > 0
							&& (
								<div
									className={styles.enabledAppsAlertContainer}
									style={{ width: headerWidthInPx }}
								>
									<SpaceTemplateEnabledAppsAlert
										apps={templateEnabledAppsToConfigureForSpace}
										onDismiss={dismissTemplateAlertForSpaceHandler}
										onAppClick={templateAlertAppClickHandler}
									/>
								</div>
							)
						}
						<div
							className={classNames(
								styles.header,
								{ [styles.header_sticky]: renderState.isInEditMode }
							)}
						>
							<div
								className={styles.headerSizeWrapper}
								style={{ width: headerWidthInPx }}
							>
								<Header
									isInEditMode={renderState.isInEditMode}
									canEdit={renderState.options.isSet && allowEditMode}
									shouldRenderBackdrop={hasBackground}
									onEnterEditMode={() => setEditMode(true)}
									onCancel={() => cancelHandler()}
									onAddTiles={() => addNewTileHandler()}
									onSave={() => saveHandler()}
									tileGroupMenuOptions={tileGroupMenuOptions}
									onCreateNewPersonalGroup={() => addNewTileGroupHandler(TileGroupType.Personal)}
									onCreateNewSharedGroup={() => addNewTileGroupHandler(TileGroupType.Shared)}
									onChangeShareGroupsOrder={() => openSharedTileGroupDefaultOrderList()}
									onChangeTileGroupVisibility={changeTileGroupVisibilityHandler}
								/>
							</div>
						</div>

						<div
							className={styles.workspace}
							ref={workspaceRef}
						>
							<GridCellPixelValuesTracker
								kind="tile"
								onChange={setTileGridCellTrackedPixelValues}
							/>

							<TileGridConfigurationTracker
								onNewConfiguration={setTileGridConfiguration}
							/>

							<TileGroupGrid
								ref={tileGroupGridRef}
								renderState={renderState}
								cellPixelValues={tileGroupGridCellPixelValues}
								tileGridCellPixelValues={tileGridCellPixelValues}
								permissions={userPermissions}
								userTimezone={userTimezone}
								tileContentDependencies={tileContentDependencies}
								onMutateRenderState={setRenderState}
								tileGroupCallbacks={{
									onAddTiles: addNewTileHandler,
									onDelete: tileGroupDeleteHandler,
									onNameChange: tileGroupNameChangeHandler,
									onResize: tileGroupResizeHandler,
									onSharedTileGroupEdit: sharedTileGroupEditHandler
								}}
								tileCallbacks={{
									onEdit: editTileHandler,
									onResize: resizeTileHandler,
									onDelete: deleteTileHandler
								}}
								isOnImage={hasBackground}
							/>
						</div>

						<MobileNavigationBar.ExtraScrollSpacing
							sortedVisibleTileGroups={sortedVisibleTileGroups}
							scrollContainerHeight={scrollContainerHeight}
							tileGroupGridCellPixelValues={tileGroupGridCellPixelValues}
						/>

						<div className={styles.poweredBy}>
							<PoweredBy
								isPoweredByTextEnabled={settings.isPoweredByTextEnabled}
								logoUrls={branding.logoUrls}
								logoMaxHeight="reduced"
							/>
						</div>
					</div>
				</div>

				<ItemCreationFab />
			</div>
			{isBusy && (
				<Portal container={document.body}>
					<BusyIndicator />
				</Portal>
			)}
			{sharedTileGroupDefaultOrderList}
			{editSharedTileGroup}
			{editTileSlider}
			{appStoreDialog}
		</>
	);
};
Workspace.displayName = "Workspace";

export type RegisterWorkspaceSubscriptionsOptions = {
	spaceAdded: (newSpace: SpaceInfo) => void;
	spaceUpdated: (space: SpaceInfo) => void;
	spaceDeleted: (spaceId: number) => void;
};

export function registerWorkspaceSubscriptions(
	options: RegisterWorkspaceSubscriptionsOptions
): SubscriptionTokenCollection {
	const {
		spaceAdded,
		spaceUpdated,
		spaceDeleted
	} = options;

	const result = new SubscriptionTokenCollection();

	const addSubscribtionToken: PubSubSubscriptionToken = workspaceSubscribe(
		WorkspaceEvent.SpaceAdded,
		(_, data) => {
			spaceAdded(data.space);
		}
	);

	result.add(addSubscribtionToken);

	const updateSubscribtionToken: PubSubSubscriptionToken = workspaceSubscribe(
		WorkspaceEvent.SpaceUpdated,
		(_, data) => {
			spaceUpdated(data.space);
		}
	);

	result.add(updateSubscribtionToken);

	const deleteSubscribtionToken: PubSubSubscriptionToken = workspaceSubscribe(
		WorkspaceEvent.SpaceDeleted,
		(_, data) => {
			spaceDeleted(data.spaceId);
		}
	);

	result.add(deleteSubscribtionToken);

	return result;
}

function computeTileGroupGridCellPixelValues(
	tileGridCellPixelValues: GridCellPixelValues,
	tileGridDimensionLimits: TileGridConfiguration["dimensionLimits"]
): GridCellPixelValues {
	const columnWidth: number = (tileGridDimensionLimits.columns * tileGridCellPixelValues.columnWidth)
		+ ((tileGridDimensionLimits.columns + 1) * tileGridCellPixelValues.columnGutter);

	const columnGutter: number = tileGridCellPixelValues.columnWidth;

	return new GridCellPixelValues(
		columnWidth,
		tileGridCellPixelValues.rowHeight,
		columnGutter,
		tileGridCellPixelValues.rowGutter
	);
}

export type WorkspacePageProps = {
	spaceId?: number;
	onNavigate: NavigationCallback;
};

const WorkspacePage = (props: WorkspacePageProps): ReactElement => {
	const { spaceId, onNavigate } = props;
	const [viewModel, setViewModel] = useState<WorkspaceViewModel | null>(null);
	const alertManager: AlertManager = useAlerts();

	usePageTitle(
		userConfiguration().branding
			.productName
	);

	const changeActiveSpaceIdHandler = useCallback(
		(spaceIdNext: number | undefined, historyKind: HistoryNavigationKind): void => {
			const navigation: Navigation = getWorkspacePageNavigation(spaceIdNext);
			onNavigate(navigation, { shouldReplace: historyKind === "replace" });
		},
		[onNavigate]
	);

	useEffect(
		(): (() => void) => {
			setViewModel(null);

			const load = async (signal: AbortSignal): Promise<void> => {
				try {
					const model: WorkspaceViewModel = await getWorkspacePageViewModel(signal)
						.catch(handlePageDataLoadingError);

					setViewModel(model);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager]
	);

	const renderContent = (): ReactElement => {
		if (viewModel === null) {
			return <BusyIndicator />;
		}

		return (
			<Workspace
				viewModel={viewModel}
				activeSpaceId={spaceId}
				onChangeActiveSpaceId={changeActiveSpaceIdHandler}
			/>
		);
	};

	return (
		<div id="main-content">
			{renderContent()}
		</div>
	);
};
WorkspacePage.displayName = "WorkspacePage";

export type WorkspacePageRouteParams = RouteParametersFromProps<
	WorkspacePageProps,
	never,
	"spaceId"
>;

export const WorkspaceForRouter = connectToRouter(
	WorkspacePage,
	{
		spaceId: {
			isRequired: false,
			type: "route",
			tryParse: tryParseInt
		},
		onNavigate: PredefinedPropOptionKind.NavigateCallback
	}
);
